<script setup>
/**
 * One task, worked through, permanently on the Tasks page.
 *
 * WHY A SECOND EXPLAINER. `WasloExplainer` above it says what a task is in prose, and
 * prose is where this concept has always been lost: an owner reads "steps your assistant
 * follows" and pictures another box to write answers in — which is the single
 * misunderstanding this piece was scheduled to fix. A worked example is not decoration
 * next to that paragraph, it is the half of the explanation that carries the SHAPE: a
 * customer sentence goes in at the top, three numbered instructions run down the middle,
 * and a written-down result comes out at the bottom. That is the whole data model, drawn.
 *
 * IT IS NOT AN EMPTY STATE. It stays on the page when the workspace already has ten tasks,
 * because "what is this screen for" is a question people ask again three weeks later, and
 * because the example is also the house style for a good trigger and a good step — the two
 * fields where a vague answer quietly produces an assistant that starts the wrong task.
 *
 * The customer's line is deliberately Kuwaiti Arabic while the frame is English: this
 * product's customers write in Arabic, and an example that only ever shows English is
 * teaching a shape nobody's inbox actually has. It sits in a `<bdi>` so the quotation
 * marks around it cannot be swallowed by the RTL run (`helpers/bidi.js` has the long
 * version of that story).
 */
import { computed } from 'vue';

import { useWasloI18n } from '../i18n.js';
import { MARKER, isolateParts } from '../helpers/bidi.js';
import { toIntlLocale } from '../helpers/intl.js';

const { t, locale } = useWasloI18n();

/** Three nodes, never one string — the middle one is the isolate. */
const customerLine = computed(() =>
  isolateParts(
    t('WASLO.TASKS.EXAMPLE.TRIGGER', { message: MARKER }),
    t('WASLO.TASKS.EXAMPLE.TRIGGER_MESSAGE')
  )
);

// The step numbers are the one place on this card a digit is rendered, and Arabic renders
// them Arabic-Indic. `toIntlLocale` first, because Chatwoot's `pt_BR` throws in `Intl`.
const numbers = computed(() => {
  const format = new Intl.NumberFormat(toIntlLocale(locale.value));
  return [1, 2, 3].map(n => format.format(n));
});

const steps = computed(() =>
  [1, 2, 3].map((n, index) => ({
    number: numbers.value[index],
    text: t(`WASLO.TASKS.EXAMPLE.STEP_${n}`),
  }))
);
</script>

<template>
  <section
    class="flex flex-col gap-4 px-5 py-5 outline outline-1 rounded-xl outline-n-weak bg-n-solid-2"
    data-waslo-task-example
  >
    <h2 class="mb-0 text-sm font-medium text-n-slate-12">
      {{ t('WASLO.TASKS.EXAMPLE.HEADING') }}
    </h2>

    <!-- The customer's message. Everything below it is the assistant's answer to it. -->
    <div class="flex items-start gap-3">
      <span
        class="grid shrink-0 place-items-center rounded-lg size-7 bg-n-alpha-2"
        aria-hidden="true"
      >
        <span class="size-4 i-lucide-message-circle text-n-slate-11" />
      </span>
      <p class="mb-0 text-sm leading-7 text-n-slate-12" dir="auto">
        <span>{{ customerLine.before }}</span
        ><bdi>{{ customerLine.value }}</bdi
        ><span>{{ customerLine.after }}</span>
      </p>
    </div>

    <!--
      The steps, indented under the message they answer. `ms-3` plus a border on the inline
      START edge draws the "and then it does this" without a second card: logical
      properties only, so it lands on the correct side in Arabic with nothing to mirror.
    -->
    <ol
      class="flex flex-col gap-3 mb-0 list-none border-s ms-3 ps-6 border-n-weak"
      data-waslo-task-example-steps
    >
      <li
        v-for="step in steps"
        :key="step.number"
        class="flex items-start gap-3"
      >
        <span
          class="grid text-xs font-medium rounded-full shrink-0 place-items-center size-6 bg-n-blue-3 text-n-blue-11 tabular-nums"
          aria-hidden="true"
        >
          {{ step.number }}
        </span>
        <span class="text-sm leading-6 text-n-slate-11" dir="auto">
          {{ step.text }}
        </span>
      </li>
    </ol>

    <div class="flex items-start gap-3">
      <span
        class="grid shrink-0 place-items-center rounded-lg size-7 bg-n-blue-3"
        aria-hidden="true"
      >
        <span class="size-4 i-lucide-clipboard-check text-n-blue-10" />
      </span>
      <p class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11" dir="auto">
        {{ t('WASLO.TASKS.EXAMPLE.RESULT') }}
      </p>
    </div>
  </section>
</template>
